import React, {useMemo} from 'react';
import {Image} from 'antd-mobile';
import {motion} from 'motion/react';
import defaultUser from '../../assets/default-user.webp';
import coinIcon from '../../assets/daily-price.webp';
import bonusIcon from '../../assets/daily-bouns.webp';
import {LeaderboardItemType} from '../../types';
import {GetYesterdayRewardAndPopupResponse} from '../../services/dailyBettingBoard';
import {useShare} from '@/common-pages/hooks/share.hooks';
import {toPriceStr} from '@components/utils';

// 呼吸灯动画配置 - 简单呼吸效果
const BREATHING_ANIMATION = {
  duration: 1,
  scaleRange: [1, 1.08, 1] as [number, number, number],
  opacityRange: [0.95, 1, 0.95] as [number, number, number],
};

const ClockSvg = (props: {className: string}) => {
  return (
    <svg className={props.className} viewBox="0 0 14 14" fill="none">
      <path
        d="M10.2354 1.0376C11.4711 1.0377 12.4326 2.00007 12.4326 3.23584C12.4325 4.01207 12.0393 4.58793 11.5811 4.95459L11.1943 5.26318C11.4208 5.84652 11.5468 6.48166 11.5469 7.14795C11.5469 8.28725 11.1741 9.29856 10.5996 10.1274L11.1055 10.6333C11.596 11.1238 11.596 11.9572 11.1055 12.4478C10.858 12.6953 10.5066 12.8159 10.2354 12.8159C10.0663 12.8159 9.90288 12.787 9.74609 12.7173C9.59153 12.6486 9.46854 12.551 9.36523 12.4478L8.73438 11.8179C8.08829 12.1203 7.37241 12.2993 6.54395 12.2993C5.7793 12.2993 5.01787 12.1208 4.35352 11.8159L3.72266 12.4478C3.47512 12.6952 3.1238 12.8159 2.85254 12.8159C2.68356 12.8159 2.52 12.7869 2.36328 12.7173C2.20881 12.6486 2.08569 12.551 1.98242 12.4478C1.49195 11.9572 1.4919 11.1238 1.98242 10.6333L2.45801 10.1567C1.76801 9.31491 1.39355 8.29027 1.39355 7.14795C1.39358 6.50415 1.50957 5.88882 1.72168 5.32178L1.3916 4.9917C0.977708 4.57776 0.581161 4.01478 0.581055 3.23584C0.581055 1.98604 1.63146 1.0376 2.7793 1.0376C3.53885 1.0377 4.21075 1.41817 4.59766 1.99854L4.79004 2.2876C5.31649 2.10003 5.88176 1.99761 6.46973 1.99756C7.07967 1.99756 7.6659 2.10696 8.20898 2.30811L8.41602 1.99854C8.80297 1.41811 9.47565 1.0376 10.2354 1.0376Z"
        fill="url(#paint0_linear_1851_1660)"
        stroke="#713A01"
      />
      <path
        d="M6.83921 7.29562C6.83921 7.36943 6.76537 7.44325 6.76537 7.44325L5.14126 9.06737C5.06744 9.14118 4.9198 9.21502 4.84596 9.21502C4.77214 9.21502 4.6245 9.1412 4.55068 9.06737C4.40303 8.91972 4.40303 8.69825 4.55068 8.55061L6.10096 7.00032V4.78561C6.10096 4.56415 6.32243 4.4165 6.47008 4.4165C6.69154 4.4165 6.83921 4.56415 6.83921 4.78561V7.29562Z"
        fill="#713A01"
      />
      <defs>
        <linearGradient
          id="paint0_linear_1851_1660"
          x1="6.50707"
          y1="1.5376"
          x2="6.50707"
          y2="12.3158"
          gradientUnits="userSpaceOnUse">
          <stop stopColor="#F0B455" />
          <stop offset="0.183688" stopColor="#FEF17F" />
          <stop offset="0.369536" stopColor="#FEFDAF" />
          <stop offset="0.60934" stopColor="#FFE764" />
          <stop offset="1" stopColor="#FFB55A" />
        </linearGradient>
      </defs>
    </svg>
  );
};

interface CurrentUserProps {
  countdown: string | null;
  currentUser?: LeaderboardItemType | null;
  loading?: boolean;
  yesterdayRewardInfo?: GetYesterdayRewardAndPopupResponse | null;
}

export default function CurrentUser({
  countdown,
  currentUser,
  loading = false,
  yesterdayRewardInfo,
}: CurrentUserProps) {
  // 判断是否显示倒计时header
  // 条件：是回归用户 AND 有倒计时数据 AND 倒计时未结束
  const shouldShowCountdownHeader = !!(
    yesterdayRewardInfo?.regressionInfo?.regressionFlag &&
    yesterdayRewardInfo?.regressionInfo.bonusRate &&
    countdown &&
    countdown !== '00:00:00'
  );
  const {doShare} = useShare(true);
  const rateText = useMemo(() => {
    const value = yesterdayRewardInfo?.regressionInfo.bonusRate || 0;
    return Math.floor(value * 100) + '%';
  }, [yesterdayRewardInfo]);
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Header with bonus points and countdown - 仅在回归用户且有倒计时未结束时显示 */}
      {shouldShowCountdownHeader && (
        <div className="bg-[#ffea98] flex items-center justify-between px-3 py-2 h-[1.875rem] w-full">
          <p className="font-semibold text-[#713a01] text-[0.75rem]">
            Earn
            <span className="text-[#f90] text-base font-extrabold tracking-[-1px] mx-0.5 inline-block transform skew-x-[-20deg]">
              {rateText}
            </span>
            Bonus Points - Countdown
          </p>
          <div className="flex items-center gap-[0.375rem]">
            <ClockSvg className="size-3.5" />
            <div className="shadow-[0px_4px_4px_0px_rgba(0,0,0,0.08)] px-0 py-0.5 w-[3.25rem]">
              <p className="font-semibold text-[#713a01] text-[0.75rem] text-center">
                {countdown}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* User ranking section */}
      <div className="relative h-[3.875rem] w-full overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <div
            className="w-full h-full bg-contain"
            style={{
              backgroundImage: `url(${require('../../assets/daily-current-bg.webp')})`,
            }}
          />
        </div>

        {/* Content container */}
        <div className="relative h-full flex items-center px-4">
          {/* Rank number */}
          <p
            className="text-[#ffea98] text-[0.875rem] font-bold"
            style={{
              textShadow: 'rgba(0,0,0,0.14) 0px 2px 1px',
            }}>
            {loading ? '--' : currentUser?.rank || '--'}
          </p>

          {/* User avatar */}
          <div className="flex items-center ml-3">
            <div className="w-10 h-10 border-2 border-[#ffeaa7] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.2)] rounded-full overflow-hidden">
              <Image
                src={currentUser?.avatar || defaultUser}
                className="w-full h-full"
                fit="cover"
              />
            </div>
          </div>

          {/* User info */}
          <div className="flex flex-col ml-2 w-[5rem]">
            <p
              className="font-semibold text-[#ffea98] text-[0.75rem]"
              style={{
                textShadow: 'rgba(0,0,0,0.38) 0px 1px 1px',
              }}>
              {loading ? 'Loading...' : currentUser?.username || 'You'}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <Image src={bonusIcon} className="w-3 h-3" />
              <p
                className="font-bold text-[#ffea98] text-[0.75rem]"
                style={{
                  textShadow: 'rgba(0,0,0,0.25) 0px 2px 1px',
                }}>
                {loading
                  ? '--'
                  : toPriceStr(currentUser?.point || 0, {
                      showCurrency: false,
                      suffixUnit: 'K',
                      fixed: 0,
                    })}
              </p>
            </div>
          </div>

          {/* Total points display */}
          <div className="flex items-center gap-1">
            <Image src={coinIcon} className="w-3 h-3" />
            <p
              className="font-bold text-[#ffea98] text-[0.75rem]"
              style={{
                textShadow: 'rgba(0,0,0,0.21) 0px 2px 1px',
              }}>
              {loading
                ? '--'
                : toPriceStr(currentUser?.bonus || 0, {
                    showCurrency: false,
                    suffixUnit: 'K',
                    fixed: 2,
                  })}
            </p>
          </div>

          {/* Free Points button */}
          <motion.div
            onClick={() => doShare()}
            className="flex items-center justify-center h-[2.1875rem] rounded-[2.5rem] border border-black/10 ml-auto px-3"
            style={{
              background:
                'linear-gradient(180deg, #FFF472 0%, #FFF9CF 9.6%, #FFE95E 43.5%, #FFB200 73.72%, #FFDD45 88.46%, #FFCB00 100%)',
              boxShadow:
                '0 0 12px 0 rgba(255, 183, 0, 0.50), inset -2px 2px 2px 0 rgba(255, 255, 255, 0.70)',
              willChange: 'transform, opacity',
            }}
            animate={{
              scale: BREATHING_ANIMATION.scaleRange,
              opacity: BREATHING_ANIMATION.opacityRange,
            }}
            transition={{
              duration: BREATHING_ANIMATION.duration,
              repeat: Infinity,
              ease: 'easeInOut',
            }}>
            <p className="text-center text-[#713A01] text-sm font-bold tracking-[-0.56px] uppercase leading-none whitespace-nowrap">
              Free Points
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
